import React, { useState, useRef, useEffect, useCallback } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import MobileReasonDisplay from "./MobileReasonDisplay";
import MobileButtonRow from "./MobileButtonRow";
import SwipeIndicators from "./SwipeIndicators";
import { getMobileReasonData } from "./MobileReasons";
import useSound from "use-sound";
import tickSoundFile from "../assets/tick.mp3";
import { Toaster } from "react-hot-toast";
import StarField from "./StarField";

const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  width: 100vw;
  position: relative;
  overflow-x: hidden;
  background: linear-gradient(135deg, rgb(21, 18, 35) 0%, rgb(7, 2, 30) 100%);
  overflow-y: auto;

  @supports (-webkit-touch-callout: none) {
    min-height: -webkit-fill-available;
  }
`;

const SwipeArea = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: calc(100% - 160px);
  z-index: 21;
  touch-action: pan-y;
  pointer-events: auto;
`;

const BackgroundWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0.5;
  width: 100vw;
  height: 100vh;
  z-index: 0;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 20;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding: 1rem;
  pointer-events: none;
`;

const ReasonSection = styled.div`
  flex: 0 0 auto;
  padding: 1rem 0;
  text-align: center;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 25;
  pointer-events: none;
`;

const PhoneSection = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow: hidden;
  z-index: 0;
  padding-top: 200px;
`;

const MobilePhoneCard = styled(motion.div).withConfig({
  shouldForwardProp: prop => prop !== "imageUrl",
})`
  width: 100vw;
  height: 150vw;
  max-width: none;
  max-height: none;
  background-image: url("${props =>
    encodeURI(props.imageUrl || "/phone-mockup.png")}");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center top;
  pointer-events: none;
  z-index: 0;
  position: relative;

  @media (max-width: 480px) {
    height: 160vw;
  }

  @media (max-width: 360px) {
    height: 170vw;
  }
`;

const GradientOverlay = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 250px;
  background: linear-gradient(
    0deg,
    rgba(5, 2, 16, 0.98) 0%,
    rgba(5, 2, 16, 0.85) 40%,
    rgba(5, 2, 16, 0.6) 70%,
    transparent 100%
  );
  z-index: 10;
  pointer-events: none;
`;

const ButtonSection = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1rem;
  padding-bottom: calc(2rem + env(safe-area-inset-bottom, 1rem));
  margin-bottom: 20px;
  pointer-events: auto;
  z-index: 100;

  /* Enhanced safe area support for various mobile browsers */
  @supports (padding: max(0px)) {
    padding-bottom: max(2rem, calc(2rem + env(safe-area-inset-bottom, 0px)));
  }

  /* Additional margin for browsers that show bottom bars */
  @media (max-height: 700px) {
    margin-bottom: 30px;
    padding-bottom: calc(2.5rem + env(safe-area-inset-bottom, 1rem));
  }

  @media (max-height: 600px) {
    margin-bottom: 40px;
    padding-bottom: calc(3rem + env(safe-area-inset-bottom, 1rem));
  }
`;

const PhoneImage = ({ currentReason }) => {
  const getCurrentImage = () => {
    if (currentReason === 0) return "/phone-mockup.png";
    const data = getMobileReasonData(currentReason);
    return data ? data.image : "/phone-mockup.png";
  };

  const currentImage = getCurrentImage();

  return (
    <AnimatePresence mode="wait">
      <MobilePhoneCard
        key={`mobile-phone-${currentReason}-${currentImage}`}
        imageUrl={currentImage}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.15, ease: "easeInOut" }}
      />
    </AnimatePresence>
  );
};

const MobileApp = ({
  currentReason,
  onReasonChange,
  cameraZ,
  currentReasonData,
}) => {
  useSound(tickSoundFile, { volume: 0.5 });
  const isFirstRender = useRef(true);
  const hasTriggeredFallbackScroll = useRef(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isSwipeActive, setIsSwipeActive] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    const hideBrowserBar = () => {
      // More aggressive scroll to hide browser bars
      window.scrollTo({ top: 1, behavior: "auto" });
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
      }, 100);
    };

    const handleFirstTouch = () => {
      if (hasTriggeredFallbackScroll.current) return;
      hasTriggeredFallbackScroll.current = true;
      hideBrowserBar();
    };

    // Auto-trigger on page load for immediate effect
    const autoHideTimeout = setTimeout(() => {
      if (!hasTriggeredFallbackScroll.current) {
        hasTriggeredFallbackScroll.current = true;
        hideBrowserBar();
      }
    }, 1000);

    // Also trigger on various interaction events
    document.addEventListener("touchstart", handleFirstTouch, { once: true });
    document.addEventListener("click", handleFirstTouch, { once: true });
    document.addEventListener("scroll", handleFirstTouch, { once: true });

    return () => {
      clearTimeout(autoHideTimeout);
      document.removeEventListener("touchstart", handleFirstTouch);
      document.removeEventListener("click", handleFirstTouch);
      document.removeEventListener("scroll", handleFirstTouch);
    };
  }, []);

  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;

    let lastHeight = window.innerHeight;

    const handleResize = () => {
      const currentHeight = window.innerHeight;
      if (Math.abs(currentHeight - lastHeight) > 50) {
        document.documentElement.style.setProperty(
          "--vh",
          `${currentHeight * 0.01}px`
        );
        lastHeight = currentHeight;
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setTimeout(handleResize, 300);
      }
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleReasonChange = useCallback(
    (number, isShuffling = false) => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        onReasonChange(number);
        return;
      }

      if (!isShuffling) {
        // play();
      }

      onReasonChange(number);
    },
    [onReasonChange]
  );

  const minSwipeDistance = 50;

  const goToNextReason = useCallback(() => {
    const newNumber =
      currentReason === 0 ? 1 : currentReason === 42 ? 1 : currentReason + 1;
    handleReasonChange(newNumber);
  }, [currentReason, handleReasonChange]);

  const goToPreviousReason = useCallback(() => {
    const newNumber =
      currentReason === 0 ? 42 : currentReason === 1 ? 42 : currentReason - 1;
    handleReasonChange(newNumber);
  }, [currentReason, handleReasonChange]);

  const onTouchStart = e => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsSwipeActive(true);
  };

  const onTouchMove = e => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setIsSwipeActive(false);
      return;
    }

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe || isRightSwipe) {
      if (isLeftSwipe) {
        goToNextReason();
      } else {
        goToPreviousReason();
      }
    }

    setIsSwipeActive(false);
  };

  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goToPreviousReason();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goToNextReason();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentReason, goToNextReason, goToPreviousReason]);

  return (
    <MobileContainer>
      <BackgroundWrapper>
        <StarField cameraZ={cameraZ} intensity={0.3} />
      </BackgroundWrapper>

      <PhoneSection>
        <PhoneImage currentReason={currentReason} />
      </PhoneSection>

      <GradientOverlay />

      <ContentWrapper>
        <ReasonSection>
          <MobileReasonDisplay reasonNumber={currentReason} />
        </ReasonSection>
      </ContentWrapper>

      <ButtonSection data-button-section>
        <MobileButtonRow
          currentNumber={currentReason}
          onReasonChange={handleReasonChange}
          onDialogStateChange={setIsDialogOpen}
        />
      </ButtonSection>

      {!isDialogOpen && (
        <SwipeArea
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        />
      )}

      {!isDialogOpen && (
        <SwipeIndicators
          isActive={isSwipeActive}
          onLeftClick={goToPreviousReason}
          onRightClick={goToNextReason}
        />
      )}

      <Toaster />
    </MobileContainer>
  );
};

export default MobileApp;
